import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { essay, about, SITE_NAME } from "../data/site";
import Contact from "./Contact";
import ReadProgress from "./ReadProgress";
import type { EssayBlock } from "../types";

const renderInline = (text: string) => {
  const parts = text.split(/(\*[^*]+\*)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith("*") && part.endsWith("*")
      ? <em key={i}>{part.slice(1, -1)}</em>
      : part
  );
};

const Block = ({ b }: { b: EssayBlock }) => {
  if (b.t === "quote") return <blockquote className="es-quote">{b.text}</blockquote>;
  if (b.t === "beat") return <p className="es-beat">{b.text}</p>;
  if (b.t === "divider") return <div className="es-divider" aria-hidden="true">&#10038;</div>;
  return <p className={"es-p" + (b.drop ? " es-drop" : "")}>{renderInline(b.text)}</p>;
};

const EssayNav = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const transitionTo = (path: string) => {
    setMenuOpen(false);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const site = document.querySelector(".site");
    if (!site || reduce) { navigate(path); return; }
    site.classList.remove("fx-in");
    site.classList.add("fx-out");
    setTimeout(() => navigate(path), 300);
  };

  const NAV_ITEMS = [
    { label: "About",    href: "/#about" },
    { label: "Work",     href: "/#work" },
    // { label: "Projects", href: "/#projects" }, // Side Quests hidden for now
    { label: "Essay",    href: "/essay",    active: true },
    { label: "Travel",   href: "/#travel" },
    { label: "Contact",  href: "/#contact" },
  ];

  const handleClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    if (href.startsWith("/#")) {
      transitionTo(href);
    } else {
      transitionTo(href);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header className="nav">
        <a className="nav-brand" href="/" onClick={(e) => { e.preventDefault(); transitionTo("/"); }}>
          {SITE_NAME}
          <span className="nav-brand-sub">SWE · Field Notes</span>
        </a>
        <nav className="nav-links" aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={"nav-link" + (item.active ? " is-active" : "")}
              onClick={(e) => handleClick(e, item.href)}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <button
          className={"nav-hamburger" + (menuOpen ? " is-open" : "")}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="nav-hamburger-line" />
          <span className="nav-hamburger-line" />
          <span className="nav-hamburger-line" />
        </button>
      </header>
      <div className={"nav-mobile-overlay" + (menuOpen ? " is-open" : "")} aria-hidden={!menuOpen}>
        {NAV_ITEMS.map((item) => (
          <a
            key={item.label}
            href={item.href}
            className={"nav-mobile-link" + (item.active ? " is-active" : "")}
            onClick={(e) => handleClick(e, item.href)}
          >
            {item.label}
          </a>
        ))}
      </div>
    </>
  );
};

const EssayPage = () => {
  const e = essay;
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    requestAnimationFrame(() => el.classList.add("fx-in"));
  }, []);

  return (
    <>
      <ReadProgress />
      <EssayNav />
      <main>
        <article className="essay">
          <header className="essay-head">
            <div className="wrap-read">
              <a
                className="page-back"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  const site = document.querySelector(".site");
                  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                  if (!site || reduce) { window.location.href = "/"; return; }
                  site.classList.remove("fx-in");
                  site.classList.add("fx-out");
                  setTimeout(() => { window.location.href = "/"; }, 300);
                }}
              >
                ← Back home
              </a>
              <div className="es-kicker">{e.kicker}</div>
              <h1 className="es-title">{e.title}</h1>
              <p className="es-standfirst">{e.standfirst}</p>
              <div className="es-byline">
                <img className="es-ava" src={about.portrait} alt={SITE_NAME} />
                <div className="es-byline-id">
                  <span className="es-by-name">{SITE_NAME}</span>
                  <span className="es-by-meta">{e.date} · {e.readTime}</span>
                </div>
              </div>
            </div>
          </header>

          <div className="essay-body">
            <div className="wrap-read">
              {e.body.map((b, i) => (
                <Block key={i} b={b} />
              ))}
              <div className="es-end" aria-hidden="true">&#10038; · &#10038; · &#10038;</div>
              <p className="es-closing">{e.closing}</p>
            </div>
          </div>
        </article>
        <Contact />
      </main>
    </>
  );
};

export default EssayPage;
